import { Link, useLocation } from "react-router-dom";
import { getHoverColour } from "@/lib/brand/hoverColour";
import type { CSSProperties, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { BrandConfig } from "@/contexts/BrandContext";
import { getFontStack, getSupportedBrandFont, getSupportedBrandFontWeight } from "@/lib/brand/fontCatalog";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";
import { getTitleGraphicPosition } from "@/lib/brand/titleGraphic";

interface Feature { title?: string; description?: string; image_url?: string; text_alignment?: string }

const HIGHLIGHT_LAYOUTS = ["alternating", "cards", "featured", "gallery"] as const;
const HIGHLIGHT_SPACING = ["compact", "standard", "spacious"] as const;

function getHighlightLayout(value: unknown) {
  return HIGHLIGHT_LAYOUTS.includes(value as typeof HIGHLIGHT_LAYOUTS[number]) ? value : "alternating";
}

function getHighlightSpacing(value: unknown) {
  return HIGHLIGHT_SPACING.includes(value as typeof HIGHLIGHT_SPACING[number]) ? value : "standard";
}

interface BrandLandingPresentationProps {
  config: BrandConfig;
  fallbackName?: string;
  preview?: boolean;
  mobile?: boolean;
}

export function BrandLandingPresentation({
  config,
  fallbackName = "Portal",
  preview = false,
  mobile = false,
}: BrandLandingPresentationProps) {
  const location = useLocation();
  const content = (config.landing_content || {}) as Record<string, any>;
  const features: Feature[] = Array.isArray(content.features) ? content.features : [];
  const highlightLayout = getHighlightLayout(content.highlights_layout);
  const highlightSpacing = getHighlightSpacing(content.highlights_spacing);
  const name = config.company_name || fallbackName;
  const colors = config.color_tokens || {};
  const bodyColor = colors.foreground || "335 41% 14%";
  const hasSubheadingColor = Boolean(colors["subheading-foreground"]);
  const backgroundGraphic = typeof content.hero_background_image_url === "string" ? content.hero_background_image_url.trim() : "";
  const graphicOpacity = typeof content.hero_background_opacity === "number" && Number.isFinite(content.hero_background_opacity)
    ? Math.min(100, Math.max(0, content.hero_background_opacity)) : 25;
  const style = {
    "--landing-graphic-position": getTitleGraphicPosition(content.hero_background_position),
    "--landing-graphic-opacity": String(graphicOpacity / 100),
    "--landing-primary": colors.primary || "335 41% 14%",
    "--landing-primary-foreground": colors["primary-foreground"] || "0 0% 100%",
    "--landing-primary-hover": getHoverColour(colors.primary || "335 41% 14%", colors["primary-hover"]),
    "--landing-background": colors.background || "0 0% 100%",
    "--landing-foreground": bodyColor,
    "--landing-heading-foreground": colors["heading-foreground"] || bodyColor,
    "--landing-subheading-foreground": colors["subheading-foreground"] || bodyColor,
    "--landing-subheading-opacity": hasSubheadingColor ? "1" : "0.68",
    "--landing-accent": colors.accent || "71 73% 76%",
    "--landing-heading-font": getFontStack(getSupportedBrandFont(config.font_config?.heading)) || "var(--brand-heading-font)",
    "--landing-body-font": getFontStack(getSupportedBrandFont(config.font_config?.body)) || "var(--brand-body-font)",
    "--landing-heading-weight": getSupportedBrandFontWeight(config.font_config?.heading_weight, "700"),
    "--landing-body-weight": getSupportedBrandFontWeight(config.font_config?.body_weight, "400"),
  } as CSSProperties;

  const loginSearch = new URLSearchParams(location.search);
  const loginTarget = loginSearch.has("brand")
    ? { pathname: "/login", search: `?${loginSearch.toString()}` }
    : "/login";
  const action = (children: ReactNode) => preview ? children : <Link to={loginTarget}>{children}</Link>;

  return (
    <div
      className={cn("brand-landing flex min-h-screen flex-col", preview && "min-h-0", mobile && "brand-landing-mobile")}
      style={style}
    >
      <header className="brand-landing-header flex items-center justify-between">
        {config.logo_url ? (
          <img src={config.logo_url} alt={name} className="brand-landing-logo object-contain" style={getBrandLogoStyle(config.logo_scale)} />
        ) : (
          <span className="brand-landing-name font-semibold">{name}</span>
        )}
        <Button asChild={!preview} size="sm" className="brand-landing-button">
          {action(<span>{content.login_label || "Sign in"}</span>)}
        </Button>
      </header>

      <main className="flex-1">
        <section className="brand-landing-hero text-center">
          {backgroundGraphic && <img src={backgroundGraphic} alt="" aria-hidden="true" className="brand-landing-title-graphic" />}
          <div className="brand-landing-hero-inner mx-auto">
          <div className="brand-landing-message">
          <div className="brand-landing-message-content">
          <h1 className="brand-landing-title font-bold">{content.hero_title || name}</h1>
          {content.hero_subheading && <p className="brand-landing-subheading mx-auto">{content.hero_subheading}</p>}
          {content.hero_subtitle && <p className="brand-landing-supporting mx-auto">{content.hero_subtitle}</p>}
          <div className="brand-landing-actions flex flex-wrap items-center justify-center">
            <Button asChild={!preview} size="lg" className="brand-landing-button">
              {action(<span>{content.cta_label || "Access your portal"}</span>)}
            </Button>
            {content.secondary_cta_label && content.secondary_cta_url && (
              <Button asChild={!preview} size="lg" variant="outline" className="brand-landing-secondary-button">
                {preview ? <span>{content.secondary_cta_label}</span> : <a href={content.secondary_cta_url}>{content.secondary_cta_label}</a>}
              </Button>
            )}
          </div>
          </div>
          </div>
          {content.hero_image_url && (
            <img src={content.hero_image_url} alt="" className="brand-landing-image w-full object-cover" />
          )}
          </div>
        </section>

        {features.length > 0 && (
          <section className={cn(
            "brand-landing-features mx-auto",
            `brand-landing-features-${highlightLayout}`,
            `brand-landing-features-${highlightSpacing}`
          )}>
            {features.map((feature, index) => (
              <article key={index} className={cn("brand-landing-feature", feature.image_url && "brand-landing-feature-with-image")}>
                {feature.image_url && (
                  <img
                    src={feature.image_url}
                    alt={feature.title || `Highlight ${index + 1}`}
                    className="brand-landing-feature-image h-full w-full object-cover"
                  />
                )}
                <div className={cn("brand-landing-feature-copy", {
                  "text-left": feature.text_alignment !== "center" && feature.text_alignment !== "right",
                  "text-center": feature.text_alignment === "center",
                  "text-right": feature.text_alignment === "right",
                })}>
                  <h3 className="font-semibold">{feature.title || "Highlight title"}</h3>
                  <p>{feature.description || "Add a short description for this highlight."}</p>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>

      <footer className="brand-landing-footer">
        <div className="mx-auto flex flex-wrap items-center justify-between">
          <span>{content.footer_text || `© ${new Date().getFullYear()} ${name}`}</span>
          {config.support_email && <a href={`mailto:${config.support_email}`}>{config.support_email}</a>}
        </div>
      </footer>
    </div>
  );
}